import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { CssBaseline, IconButton, ThemeProvider, Tooltip } from "@mui/material";
import { DarkModeOutlined, LightModeOutlined } from "@mui/icons-material";
import { getTheme } from "../theme";

// The chosen theme is per browser. Until the user picks one, it's light.
const STORAGE_KEY = "themeMode";

const readStored = () => {
	try {
		const v = localStorage.getItem(STORAGE_KEY);
		return v === "light" || v === "dark" ? v : null;
	} catch {
		return null;
	}
};

const ThemeModeContext = createContext({ mode: "light", toggleMode: () => {} });

export function ThemeModeProvider({ children }) {
	const [chosen, setChosen] = useState(readStored);
	const mode = chosen || "light";

	useEffect(() => {
		document.documentElement.dataset.theme = mode;
	}, [mode]);

	const value = useMemo(
		() => ({
			mode,
			toggleMode: () => {
				const next = mode === "dark" ? "light" : "dark";
				setChosen(next);
				try {
					localStorage.setItem(STORAGE_KEY, next);
				} catch {
					// Private mode or blocked storage: the choice lasts for this visit only.
				}
			},
		}),
		[mode],
	);
	const theme = useMemo(() => getTheme(mode), [mode]);

	return (
		<ThemeModeContext.Provider value={value}>
			<ThemeProvider theme={theme}>
				<CssBaseline enableColorScheme />
				{children}
			</ThemeProvider>
		</ThemeModeContext.Provider>
	);
}

export const useThemeMode = () => useContext(ThemeModeContext);

export function ThemeToggle({ sx, className }) {
	const { mode, toggleMode } = useThemeMode();
	const label = mode === "dark" ? "Switch to light theme" : "Switch to dark theme";
	return (
		<Tooltip title={label}>
			<IconButton onClick={toggleMode} aria-label={label} className={className} sx={{ color: "text.secondary", ...sx }}>
				{mode === "dark" ? <LightModeOutlined fontSize="small" /> : <DarkModeOutlined fontSize="small" />}
			</IconButton>
		</Tooltip>
	);
}
